/**
 * 主题切换：单个图标按钮 + 下拉。
 *
 * 上一版用的是三态分段控件，用户反馈「三个图标平铺着，看不出是一个控件」——
 * 在 48px 高的顶栏里，Segmented 的容器背景太弱，读起来就是三个独立按钮。
 * 换成下拉后，顶栏只占一个图标位，而三个选项在菜单里带**文字标签**，
 * 比一个显示器图标要表达「跟随系统」清楚得多。
 *
 * 按钮图标反映的是**用户的选择**（system 就显示显示器图标），不是解析结果 ——
 * 否则跟随系统时用户无法确认自己到底选没选过。
 */

import { Button, Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import type { ThemeMode } from '@/theme';
import { useSettingsStore } from '@/store/settings';

const ICON: Record<ThemeMode, React.ReactNode> = {
  light: <SunOutlined />,
  dark: <MoonOutlined />,
  system: <DesktopOutlined />,
};

const LABEL: Record<ThemeMode, string> = {
  light: '浅色',
  dark: '深色',
  system: '跟随系统',
};

const ITEMS: MenuProps['items'] = (['light', 'dark', 'system'] as const).map((m) => ({
  key: m,
  icon: ICON[m],
  label: LABEL[m],
}));

export function ThemeSwitch() {
  const themeMode = useSettingsStore((s) => s.themeMode);
  const resolvedMode = useSettingsStore((s) => s.resolvedMode);
  const setThemeMode = useSettingsStore((s) => s.setThemeMode);

  const hint =
    themeMode === 'system'
      ? `跟随系统（当前${resolvedMode === 'dark' ? '深色' : '浅色'}）`
      : LABEL[themeMode];

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        items: ITEMS,
        selectable: true,
        selectedKeys: [themeMode],
        onClick: ({ key }) => setThemeMode(key as ThemeMode),
      }}
    >
      <Tooltip title={`主题：${hint}`} placement="bottom">
        <Button type="text" size="small" aria-label={`主题：${hint}`} icon={ICON[themeMode]} />
      </Tooltip>
    </Dropdown>
  );
}
